// @ts-strict-ignore
import { v4 as uuidv4 } from 'uuid';
import { JourneyNodeTypesMap } from '@usealloy/typegen';

import type { JourneyStep } from '~/application-review/redux/types';
import type { EntityGroupRelationship } from '~/types/entity';
import { ENTITY_TYPES } from '~/types/entity';

import type {
  InvestigationResponse,
  TagEvidenceResponse,
} from './redux/types';
import type {
  Investigation,
  InvestigationAgent,
  InvestigationEntityMap,
  InvestigationEvent,
  InvestigationEvidences,
  InvestigationNote,
  InvestigationReview,
} from './types';

const formatAgent = (assignedAgent: InvestigationResponse['_embedded']['assigned_agent']): InvestigationAgent => ({
  email: assignedAgent.email,
  firstName: assignedAgent.first_name,
  lastName: assignedAgent.last_name,
  token: assignedAgent.token,
});

const formatInvestigationReviews = (reviews: InvestigationResponse['_embedded']['reviews'] = []): InvestigationReview[] => (
  reviews.map(review => ({
    newInvestigationState: review.new_investigation_state,
    newInvestigationStatus: review.new_investigation_status,
    note: review.note,
    outcome: review.outcome,
    outcomeIcon: review.outcome_icon,
    outcomeColor: review.outcome_color ? `#${review.outcome_color}` : null,
    previousInvestigationState: review.previous_investigation_state,
    previousInvestigationStatus: review.previous_investigation_status,
    reason: review.reason,
    reviewedAt: new Date(review.reviewed_at).getTime() / 1000,
    reviewedBy: review.reviewed_by ? formatAgent(review.reviewed_by) : null,
    uiReviewToken: uuidv4(),
    investigationStatusColor: review.status_color ? `#${review.status_color}` : null,
    investigationStatusIcon: review.status_icon,
  })).sort((a, b) => b.reviewedAt - a.reviewedAt)
);

const formatEntities = (entities: InvestigationResponse['_embedded']['entities'] = {}): InvestigationEntityMap => (
  Object.keys(entities).reduce((formattedEntities, entityToken) => {
    const entity = entities[entityToken];

    formattedEntities[entityToken] = {
      entityType: entity.type,
      entityToken: entity.entity_group_token ?? entity.entity_token,
      entityName: entity.entity_group_name ?? entity.entity_name,
      meta: entity.meta,
      externalIngestionIdentifier: entity.external_ingestion_identifier,
      recentAction: entity.recent_action,
      activePersonVersionId: entity.active_person_version_id,
      externalEntityIdentifier: entity.external_entity_identifier,
      customerPartnerId: entity.customer_partner_id,
      activeBusinessVersionId: entity.active_business_version_id,
      riskScore: entity.risk_score,
      entityGroupRelationships: entity.entity_group_relationships?.map(rel => ({
        entityToken: rel.entity_token,
        entityName: rel.name,
        entityType: rel.type,
      })),
    };

    return formattedEntities;
  }, {})
);

const formatEntityGroupRelationships = (relationships: EntityGroupRelationship[] = []) => (
  relationships.reduce((formattedRelationships, relationship) => {
    formattedRelationships[relationship.entity_token] = {
      entityToken: relationship.entity_token,
      entityName: relationship.name,
      entityType: relationship.type,
      ownershipPercentage: relationship.ownership_percentage ?? null,
      roles: relationship.roles ?? [],
    };

    return formattedRelationships;
  }, {})
);

const formatSingleTag = (tag: TagEvidenceResponse) => ({
  addedBy: tag.added_by ? formatAgent(tag.added_by) : null,
  createdAt: new Date(tag.created_at).getTime() / 1000,
  isOutcomeReason: tag.is_outcome_reason,
  tagColor: `#${tag.tag_color}`,
  tagName: tag.tag_name,
  investigationNoteId: tag.investigation_note_id,
});

const formatInvestigationEvidences = (evidences: InvestigationResponse['_embedded']['evidences']): InvestigationEvidences => ({
  journeyAlerts: (evidences?.journey_alerts ?? []).map(journeyAlert => ({
    journeyApplicationToken: journeyAlert.journey_application_token,
    journeyToken: journeyAlert.journey_token,
  })),
  tags: (evidences?.tags ?? []).reduce((tags, tag) => {
    tags.push(formatSingleTag(tag));
    return tags;
  }, []),
});

const formatInvestigationNotes = (
  notes: InvestigationResponse['_embedded']['notes'],
  tags: InvestigationResponse['_embedded']['evidences']['tags'],
): InvestigationNote[] => (
  (notes ?? []).map(note => {
    const noteTags = tags.filter(
      ({ investigation_note_id }) => (
        investigation_note_id === note.investigation_note_id
      ),
    ).map(tag => formatSingleTag(tag));

    const noteTimestamp = new Date(note.created_at).getTime() / 1000;

    return {
      createdAt: noteTimestamp,
      createdBy: formatAgent(note.created_by),
      entityName: note.entity_name,
      entityToken: note.entity_token,
      text: note.text,
      isInvestigationLevelNote: note.is_investigation_level_note,
      isSystemAgent: note.is_system_agent,
      isTest: note.is_test,
      tags: noteTags,
    };
  })
);

const formatInvestigationEvents = (
  events: InvestigationResponse['_embedded']['events'],
): InvestigationEvent[] => (
  (events ?? []).map(event => ({
    ...event,
    agent: event.agent ? formatAgent(event.agent) : null,
    assignedAgent: event.assigned_agent ? formatAgent(event.assigned_agent) : null,
  }))
);

/* eslint-disable no-underscore-dangle */
export const formatInvestigation = (investigation: InvestigationResponse): Investigation => {
  const {
    journeyApplicationTokens,
    hasMultiEntityAlerts,
  } = (investigation._embedded.evidences?.journey_alerts ?? []).reduce((acc, alert) => {
    acc.journeyApplicationTokens.push(alert.journey_application_token);

    if (alert.journey_is_multi_entity) {
      acc.hasMultiEntityAlerts = true;
    }

    return acc;
  }, {
    journeyApplicationTokens: [],
    hasMultiEntityAlerts: false,
  });

  // Find entity group in a single iteration for consistency with formatEntities
  let entityGroupEntity;
  let isGroupInvestigation = false;
  let entityGroupEntities = {};

  for (const entity of Object.values(investigation._embedded.entities)) {
    if (entity.type === ENTITY_TYPES.entity_group) {
      entityGroupEntity = entity;
      isGroupInvestigation = true;

      entityGroupEntities = {
        ...entityGroupEntities,
        ...formatEntityGroupRelationships(entity.entity_group_relationships),
      };
      break;
    }
  }

  const numEntityGroupMembers = entityGroupEntity?.entity_group_relationships?.length ?? 0;

  return {
    investigationCreatedAt: new Date(investigation.created_at).getTime() / 1000,
    investigationClosedAt:
      investigation.closed_at ? new Date(investigation.closed_at).getTime() / 1000 : null,
    investigationArchivedAt:
      investigation.archived_at ? new Date(investigation.archived_at).getTime() / 1000 : null,
    investigationDocuments: investigation._embedded.documents,
    entities: formatEntities(investigation._embedded.entities),
    investigationAssignedAgent:
      investigation._embedded.assigned_agent?.token
        ? formatAgent(investigation._embedded.assigned_agent)
        : null,
    investigationEvents: formatInvestigationEvents(investigation._embedded.events),
    investigationEvidences: formatInvestigationEvidences(investigation._embedded.evidences),
    investigationName: investigation.name,
    investigationNotes: formatInvestigationNotes(
      investigation._embedded.notes,
      investigation._embedded.evidences.tags,
    ),
    investigationOutcome: investigation.outcome,
    investigationOutcomeIcon: investigation.outcome_icon,
    investigationOutcomeColor: investigation.outcome_color ? `#${investigation.outcome_color}` : null,
    investigationReason: investigation.reason,
    investigationReviews: formatInvestigationReviews(investigation._embedded.reviews),
    investigationSource: investigation.source,
    investigationState: investigation.state,
    investigationStatus: investigation.status,
    investigationStatusIcon: investigation.status_icon,
    investigationStatusColor: investigation.status_color ? `#${investigation.status_color}` : null,
    investigationTags: (investigation._embedded.tags ?? []).map(formatSingleTag),
    investigationToken: investigation.investigation_token,
    investigationType: investigation.type,
    investigationUpdatedAt: new Date(investigation.updated_at).getTime() / 1000,
    journeyApplicationTokens,
    investigationIsSandbox: investigation.is_sandbox ?? false,
    numEntityGroupMembers,
    isGroupInvestigation,
    entityGroupEntities,
    hasMultiEntityAlerts,
  };
};
/* eslint-enable no-underscore-dangle */

export const formatDatetime = dateString => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
};

export const formatJourneySteps = ({
  journeySteps: journeyStepsInput = [],
  terminalReconciliationNodeId = null,
}: {
  journeySteps: JourneyStep[];
  terminalReconciliationNodeId?: string;
}) => {
  // Journey steps should be displayed in reverse-chronological order starting from bottom
  const journeySteps = [...journeyStepsInput].reverse();

  return journeySteps.map(step => {
    /**
     * For multi-entity Alerts, we only want the post EOJ recon manual review to be displayed
     * in the Group Checks section, not in the individual entity sections.
     * So if we find reviews attached to the EOJ recon WF node, we remove them.
     */
    if (
      terminalReconciliationNodeId
      && step.nodeType === JourneyNodeTypesMap.RECONCILIATION_WORKFLOW
      && step.nodeId === terminalReconciliationNodeId
    ) {
      return {
        ...step,
        review: null,
        reviews: [],
      };
    }

    if (step.reviews) {
      // Reviews should also be displayed in reverse chronological order, starting from bottom
      const journeyStepReviews = [...step.reviews].reverse().map(review => ({
        ...review,
        /**
         * In some future state we will likely need to fetch and set legacy Journey Alert review data
         * completed via Cases, but for MVP we will pull the data we can from the current JA event return
         * as a graceful fallback.
         *
         * This also satisfies part of the UI while the Alert component triggers a fetch for the full
         * data for the Alert Manual Review.
         */
        outcome: review.status !== 'Pending' ? review.status : null,
        reviewedAt: review.visitedNodeAt,
      }));

      return {
        ...step,
        // Copy data to the review object representing the most recent review
        // Should be first one in array after reverse() call above
        review: journeyStepReviews?.[0],
        reviews: journeyStepReviews,
      };
    }

    return step;
  });
};
